/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { DatePicker, TextField, InputLabel, AddressAutocomplete, Select, CheckboxesGroup, PronounSelectControl } from 'components/elements';
import { useFetchEmploymentsStatus, useFetchCivilStatus, useFetchPreferredCommunications, useFetchConfiguration } from 'hooks';
import { patterns } from 'const';
import { validators } from 'utils';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function ClientInfoForm({ onSubmit, formId, ...restProps }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const { data: employmentStatuses } = useFetchEmploymentsStatus();
  const { data: civilStatuses } = useFetchCivilStatus();
  const { handleSubmit, control, watch } = useForm({
    mode: 'onSubmit',
    defaultValues: restProps,
  });
  const mobileNumber = watch('mobileNumber');
  const homeNumber = watch('homeNumber');
  const { isEnabledField, isRequiredField } = useFetchConfiguration();

  const { data: preferredCommunicationOptions } = useFetchPreferredCommunications({
    isMobileNumberFilled: mobileNumber.length,
    isHomeNumberFilled: homeNumber.length,
    isHomeNumberVisible: isEnabledField('homeNumber'),
    isMobileNumberVisible: isEnabledField('mobileNumber'),
  });

  return (
    <div className={classes.clientInfoFormWrapper}>
      <h1 className={classes.formHeader}>{t('client_information')}</h1>
      <p className={classes.tip}>
        {t('please_complete_the_form_below_to_request_a_first_appointment')}
        <br />
        {t('we_use_this_information_so_that_we_can_best_match_you_with_a_therapist_who_best_meets_your_needs')}
        <br />
        {t('please_be_assured_that_all_the_information_will_be_kept_completely_confidential')}
      </p>
      <form id={formId} className={classes.clientInfoForm} onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.multiInputsWrapper}>
          {isEnabledField('firstName') && (
            <div className={classes.field}>
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: {
                    value: isRequiredField('firstName'),
                    message: t('please_fill_in_this_field'),
                  },
                }}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <TextField
                    error={!!error}
                    helperText={error ? error.message : null}
                    {...restField}
                    label={<InputLabel text={t('first_name')} required={isRequiredField('firstName')} />}
                  />
                )}
              />
            </div>
          )}
          {isEnabledField('lastName') && (
            <div className={classes.field}>
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: {
                    value: isRequiredField('lastName'),
                    message: t('please_fill_in_this_field'),
                  },
                }}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <TextField
                    error={!!error}
                    helperText={error ? error.message : null}
                    {...restField}
                    label={<InputLabel text={t('last_name')} required={isRequiredField('lastName')} />}
                  />
                )}
              />
            </div>
          )}
        </div>
        <div className={classes.multiInputsWrapper}>
          {isEnabledField('preferredName') && (
            <div className={classes.field}>
              <Controller
                name="preferredName"
                control={control}
                rules={{
                  required: {
                    value: isRequiredField('preferredName'),
                    message: t('please_fill_in_this_field'),
                  },
                  maxLength: {
                    value: 25,
                    message: t('you_cant_use_more_than_number_characters', { number: 25 }),
                  },
                }}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <TextField
                    error={!!error}
                    helperText={error ? error.message : null}
                    {...restField}
                    label={<InputLabel text={t('preferred_name')} required={isRequiredField('preferredName')} />}
                  />
                )}
              />
            </div>
          )}
          {isEnabledField('personalPronoun') && (
            <div className={classes.field}>
              <PronounSelectControl
                name="personalPronoun"
                control={control}
                required={isRequiredField('personalPronoun')}
                isEditMode={isEnabledField('personalPronoun')}
                rules={{
                  required: {
                    value: isRequiredField('personalPronoun'),
                    message: t('please_fill_in_this_field'),
                  },
                }}
              />
            </div>
          )}
        </div>
        <div className={classes.multiInputsWrapper}>
          {isEnabledField('email') && (
            <div className={classes.emailWrapper}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: {
                    value: isRequiredField('email'),
                    message: t('please_fill_in_this_field'),
                  },
                  pattern: {
                    value: patterns.email,
                    message: t('email_invalid'),
                  },
                }}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <TextField
                    error={!!error}
                    helperText={error ? error.message : null}
                    {...restField}
                    label={<InputLabel text={t('email_address')} required={isRequiredField('email')} />}
                  />
                )}
              />
            </div>
          )}
          {isEnabledField('birthDate') && (
            <div className={classes.field}>
              <Controller
                name="birthDate"
                control={control}
                rules={{
                  required: {
                    value: isRequiredField('birthDate'),
                    message: t('please_fill_in_this_field'),
                  },
                  validate: (value) => {
                    if (value && !validators.dateFormatValidator(value)) {
                      return t('invalid_date_of_birth_entry');
                    }
                    return null;
                  },
                }}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <DatePicker
                    error={!!error}
                    helperText={error ? error.message : null}
                    {...restField}
                    label={<InputLabel text={t('birthday')} required={isRequiredField('birthDate')} />}
                  />
                )}
              />
            </div>
          )}
        </div>
        <div className={classes.multiInputsWrapper}>
          {isEnabledField('mobileNumber') && (
            <div className={classes.field}>
              <Controller
                name="mobileNumber"
                control={control}
                rules={{
                  required: {
                    value: isRequiredField('mobileNumber'),
                    message: t('please_fill_in_this_field'),
                  },
                  maxLength: {
                    value: 20,
                    message: t('you_cant_use_more_than_number_characters', { number: 20 }),
                  },
                }}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <TextField
                    error={!!error}
                    helperText={error ? error.message : null}
                    {...restField}
                    type="number"
                    label={<InputLabel text={t('mobile_number')} required={isRequiredField('mobileNumber')} />}
                  />
                )}
              />
            </div>
          )}
          {isEnabledField('homeNumber') && (
            <div className={classes.field}>
              <Controller
                name="homeNumber"
                control={control}
                rules={{
                  required: {
                    value: isRequiredField('homeNumber'),
                    message: t('please_fill_in_this_field'),
                  },
                  maxLength: {
                    value: 20,
                    message: t('you_cant_use_more_than_number_characters', { number: 20 }),
                  },
                }}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <TextField
                    error={!!error}
                    helperText={error ? error.message : null}
                    {...restField}
                    type="number"
                    label={<InputLabel text={t('home_number')} required={isRequiredField('homeNumber')} />}
                  />
                )}
              />
            </div>
          )}
        </div>
        {isEnabledField('preferredCommunication') && (
          <div className={classes.preferredCommunication}>
            <Controller
              name="preferredCommunication"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('please_fill_in_this_field'),
                },
                validate: (value) => {
                  if (!validators.containsEmailAndSMSConsent(value)) {
                    return t('email_and_mobile_consent');
                  }
                  return null;
                },
              }}
              render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                <CheckboxesGroup
                  options={preferredCommunicationOptions}
                  error={!!error}
                  helperText={error ? error.message : null}
                  optionClasses={classes.checkboxOption}
                  label={
                    <>
                      <InputLabel text={t('choose_your_preferred_method_s_of_communication')} required />
                      <Typography className={classes.preferredCommunicationLabel} variant="body2" component="p">
                        {t('the_selection_s_below_will_be_used_for_automated_appointment_reminders_and_notifications')}
                      </Typography>
                    </>
                  }
                  {...restField}
                />
              )}
            />
          </div>
        )}
        {isEnabledField('formattedAddress') && (
          <>
            <div className={classes.addressWrapper}>
              <Controller
                name="formattedAddress"
                control={control}
                rules={{
                  required: {
                    value: isRequiredField('formattedAddress'),
                    message: t('please_fill_in_this_field'),
                  },
                }}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <AddressAutocomplete
                    error={!!error}
                    helperText={error ? error.message : null}
                    {...restField}
                    label={<InputLabel text={t('address')} required={isRequiredField('formattedAddress')} />}
                  />
                )}
              />
            </div>
            <div className={classes.multiInputsWrapper}>
              <div className={classes.field}>
                <Controller
                  name="aptSuiteUnit"
                  control={control}
                  render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                    <TextField error={!!error} helperText={error ? error.message : null} {...restField} label={<InputLabel text={t('apt_suite_flr')} />} />
                  )}
                />
              </div>
            </div>
          </>
        )}
        <div className={classes.multiInputsWrapper}>
          {isEnabledField('civilStatus') && (
            <div className={classes.field}>
              <Controller
                name="civilStatus"
                control={control}
                rules={{
                  required: {
                    value: isRequiredField('civilStatus'),
                    message: t('please_fill_in_this_field'),
                  },
                }}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <Select
                    options={civilStatuses}
                    defaultValue=""
                    label={<InputLabel text={t('civil_status')} required={isRequiredField('civilStatus')} />}
                    helperText={error ? error.message : null}
                    error={!!error}
                    {...restField}
                  />
                )}
              />
            </div>
          )}
          {isEnabledField('employmentStatus') && (
            <div className={classes.field}>
              <Controller
                name="employmentStatus"
                control={control}
                rules={{
                  required: {
                    value: isRequiredField('employmentStatus'),
                    message: t('please_fill_in_this_field'),
                  },
                }}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <Select
                    options={employmentStatuses}
                    defaultValue=""
                    label={<InputLabel text={t('employment')} required={isRequiredField('employmentStatus')} />}
                    helperText={error ? error.message : null}
                    error={!!error}
                    {...restField}
                  />
                )}
              />
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

ClientInfoForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  formId: PropTypes.string.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  preferredName: PropTypes.string,
  birthDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(DateTime)]),
  email: PropTypes.string,
  mobileNumber: PropTypes.string,
  homeNumber: PropTypes.string,
  aptSuiteUnit: PropTypes.string,
  formattedAddress: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(PropTypes.any)]),
  employmentStatus: PropTypes.string,
  civilStatus: PropTypes.string,
  preferredCommunication: PropTypes.arrayOf(PropTypes.any),
  isrequestAppointment: PropTypes.bool,
};

ClientInfoForm.defaultProps = {
  firstName: '',
  lastName: '',
  preferredName: '',
  birthDate: null,
  email: '',
  mobileNumber: '',
  homeNumber: '',
  formattedAddress: '',
  aptSuiteUnit: '',
  employmentStatus: '',
  civilStatus: '',
  preferredCommunication: [],
  isrequestAppointment: true,
};

export default ClientInfoForm;
